/**
 * Entity-neutral share orchestration (PRD-6.1 "one model for lists and
 * scenarios", SE-8313 → SE-8356).
 *
 * Owns everything a Share surface needs that does not depend on WHICH object
 * is shared: the signed-in user (never offered as an invitee), the invite row
 * state (search, picked suggestion, role, inline error, in-flight guard), the
 * per-row pending set, the optimistic `cache.modify` on a role change and the
 * background refetch of the surfaces that show a Shared badge. What DOES
 * depend on the object — the grants read, the three writes with their cache
 * updates, the invite pool and its exclusions — comes in through a
 * {@link ShareGrantsAdapter}: `useShareWatchlist` supplies the watchlist one,
 * `useShareScenario` the scenario one.
 *
 * SE-8359 (PV17) adds the group arm — "Everyone at {organization}" as one
 * candidate and one WHO HAS ACCESS line — through {@link useShareGroupGrants},
 * keyed on the adapter's `entityType`; GROUP presence rows are hidden from
 * the people, INACTIVE members are kept and marked, and a person the group
 * outranks carries the effective-role caption (R06 §3, §5).
 * @module features/scouting/hooks/useShareGrants
 */

import { useApolloClient } from "@apollo/client";
import { useCallback, useMemo, useState } from "react";

import {
  type ShareEntityType,
  type UserWatchlistGrantSource,
  UserWatchlistRole,
  useAuthGetUserQuery,
} from "@/generated/graphql";
import {
  groupGrantFailureMessage,
  useShareGroupGrants,
} from "@/features/scouting/hooks/useShareGroupGrants";
import {
  effectiveRoleCaption,
  groupCandidateMatches,
  isPresenceRow,
  type ShareGroupAccessRowItem,
  type ShareGroupCandidate,
} from "@/features/scouting/utils/shareGroupGrants";
import {
  combineShareAccessStates,
  isShareGrantRole,
  resolveInvitee,
  type ShareAccessRowItem,
  type ShareAccessState,
  type ShareCandidateItem,
  type ShareGrantRole,
} from "@/features/scouting/utils/shareWatchlistGrants";
import { useToastNotification } from "@/hooks/useToastNotification";

/** Suggestions shown under the invite search. */
const MAX_SUGGESTIONS = 6;

/** Sort weight: owner first, then editors, then viewers. */
const ROLE_RANK: Readonly<Record<UserWatchlistRole, number>> = {
  [UserWatchlistRole.Owner]: 0,
  [UserWatchlistRole.Editor]: 1,
  [UserWatchlistRole.Viewer]: 2,
};

/**
 * One grant with its display name, before pending state is layered on.
 * `userWatchlistId` is the grant row id whatever the entity (a `UserWatchlist`
 * or a `UserScenario` id) — the field keeps its SE-8313 name because the
 * access-row molecule and its test ids are keyed on it.
 */
export interface ShareGrantRow {
  readonly userWatchlistId: string;
  readonly userId: string;
  readonly name: string;
  readonly role: UserWatchlistRole;
  /** DIRECT (a grant) or GROUP (a presence row, hidden) — SE-8359. */
  readonly grantSource?: UserWatchlistGrantSource | null;
  /** Whether the membership behind the grant is ACTIVE; absent = yes. */
  readonly isMembershipActive?: boolean | null;
}

const byRoleThenName = (left: ShareGrantRow, right: ShareGrantRow): number =>
  ROLE_RANK[left.role] - ROLE_RANK[right.role] ||
  left.name.localeCompare(right.name);

/** What the shared object supplies to the orchestration. */
export interface ShareGrantsAdapter {
  /** Id of the shared object; keys the cross-surface in-flight invite guard. */
  readonly entityId: string;
  /** `WATCHLIST` or `SCENARIO` — what the group documents are keyed on (SE-8359). */
  readonly entityType: ShareEntityType;
  /** Noun in the "You already have access to this …" copy. */
  readonly entityNoun: string;
  /** `__typename` of a grant row, for the optimistic role write. */
  readonly grantTypename: string;
  /** Every grant on the object, owner included. */
  readonly members: readonly ShareGrantRow[];
  /** Whether the grants are known yet. */
  readonly accessState: ShareAccessState;
  /** People the owner may invite (already-granted people are filtered here). */
  readonly organizationUsers: readonly ShareCandidateItem[];
  /** People the object refuses as invitees (e.g. Tier 3 on a scenario). */
  readonly excludedUsers: readonly ShareCandidateItem[];
  /** Caption shown when the search matches only excluded people. */
  readonly excludedCaption: string | null;
  /** Query names refetched after every write (Shared badges, counts). */
  readonly refetchQueryNames: readonly string[];
  /** Sends the invite and updates the cached grants. */
  readonly invite: (
    candidate: ShareCandidateItem,
    role: ShareGrantRole
  ) => Promise<void>;
  /** Sends the role change; the optimistic write is done by the orchestration. */
  readonly changeRole: (grantId: string, role: ShareGrantRole) => Promise<void>;
  /** Revokes the grant and drops it from the cache. */
  readonly revoke: (grantId: string) => Promise<void>;
  /**
   * Inline copy for a refused invite. Defaults to a generic retry line; the
   * scenario adapter renders the backend's own refusal verbatim.
   */
  readonly inviteFailureMessage?: (
    candidate: ShareCandidateItem,
    error: unknown
  ) => string;
}

/** Arguments for {@link useShareGrants}. */
export interface UseShareGrantsArgs {
  /** Whether the consuming surface (modal/sheet) is open. */
  readonly isOpen: boolean;
  /** Name of the shared object (toast copy). */
  readonly entityName: string;
  /** Dismiss handler — Done and the chrome's close both call it. */
  readonly onClose: () => void;
  /** The object-specific data and writes. */
  readonly adapter: ShareGrantsAdapter;
}

/** Everything a Share surface (AlertDialog or sheet) needs to render and act. */
export interface UseShareGrantsResult {
  /** WHO HAS ACCESS rows, owner first. */
  readonly accessRows: readonly ShareAccessRowItem[];
  /**
   * Whether the access rows are known: "loading" until the grants query has
   * answered once, "error" when it failed with nothing cached, else "ready".
   * Only "ready" may be read as "nobody else has access".
   */
  readonly accessState: ShareAccessState;
  /** Search-filtered people who can still be invited (empty without a query). */
  readonly candidates: readonly ShareCandidateItem[];
  /** Caption under the invite row, or null (e.g. only Tier 3 matched). */
  readonly inviteCaption: string | null;
  /** Current invite search text. */
  readonly searchQuery: string;
  /** The suggestion the owner picked, if any. */
  readonly selectedCandidate: ShareCandidateItem | null;
  /**
   * "Everyone at {organization}" while the owner may still invite it and the
   * query is empty or matches its label (SE-8359); null otherwise.
   */
  readonly groupCandidate: ShareGroupCandidate | null;
  /** The group the owner picked, if any. */
  readonly selectedGroup: ShareGroupCandidate | null;
  /** WHO HAS ACCESS group lines, rendered above the people. */
  readonly groupRows: readonly ShareGroupAccessRowItem[];
  /** Whether anyone besides the owner — a person or the group — has access. */
  readonly hasSharees: boolean;
  /** Role the next invite grants. */
  readonly inviteRole: ShareGrantRole;
  /** Inline invite error, or null. */
  readonly inviteError: string | null;
  /** Whether an invite mutation is in flight. */
  readonly isInviting: boolean;
  /** Invite search change handler. */
  readonly onSearchChange: (next: string) => void;
  /** Picks a suggestion (keyed by userId). */
  readonly onSelectCandidate: (userId: string) => void;
  /** Picks the group candidate. */
  readonly onSelectGroup: () => void;
  /** Clears the picked suggestion (person or group). */
  readonly onClearCandidate: () => void;
  /** Changes the group's role (Select value); refusals are toasted verbatim. */
  readonly onChangeGroupRole: (
    groupGrantId: string,
    value: string
  ) => Promise<void>;
  /** Revokes the group grant. */
  readonly onRevokeGroup: (groupGrantId: string) => Promise<void>;
  /** Changes the role the next invite grants (Select value). */
  readonly onInviteRoleChange: (value: string) => void;
  /** Sends the invite, or shows an inline error without a mutation. */
  readonly onInvite: () => Promise<void>;
  /** Changes a sharee's role (Select value), rolling back on error. */
  readonly onChangeRole: (
    userWatchlistId: string,
    value: string
  ) => Promise<void>;
  /** Revokes a sharee's access. */
  readonly onRevoke: (userWatchlistId: string) => Promise<void>;
  /** Closes the surface. */
  readonly onDone: () => void;
}

/**
 * Invites in flight across every mounted Share surface, keyed by
 * `<entityId>:<userId>`. Closing a surface unmounts its hook while the
 * mutation is still pending; a reopened surface would otherwise let the
 * same person be invited twice before the first request settles.
 */
const inFlightInvites: { current: ReadonlySet<string> } = {
  current: new Set(),
};

const defaultInviteFailureMessage = (candidate: ShareCandidateItem): string =>
  `Couldn't invite ${candidate.name}. Try again.`;

/**
 * Owns the invite row state and the per-row writes for sharing one object,
 * with the object-specific reads and writes supplied by the adapter.
 * @param args - Hook arguments (see {@link UseShareGrantsArgs})
 * @param args.isOpen - Whether the consuming surface is open
 * @param args.entityName - Name of the shared object (toast copy)
 * @param args.onClose - Dismiss handler
 * @param args.adapter - The object-specific data and writes
 * @returns The render state + handlers for a Share surface.
 */
export const useShareGrants = ({
  isOpen,
  entityName,
  onClose,
  adapter,
}: UseShareGrantsArgs): UseShareGrantsResult => {
  const toast = useToastNotification();
  const apolloClient = useApolloClient();

  const { data: meData } = useAuthGetUserQuery({ skip: !isOpen });
  const currentUserId = meData?.me?.id;

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCandidate, setSelectedCandidate] =
    useState<ShareCandidateItem | null>(null);
  const [selectedGroup, setSelectedGroup] =
    useState<ShareGroupCandidate | null>(null);
  const [inviteRole, setInviteRole] = useState<ShareGrantRole>(
    UserWatchlistRole.Viewer
  );
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [isInviting, setIsInviting] = useState(false);
  const [pendingGrantIds, setPendingGrantIds] = useState<ReadonlySet<string>>(
    () => new Set()
  );

  /**
   * Reset the invite row whenever the surface opens or closes, using React's
   * "adjust state during render" pattern rather than an effect.
   */
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (wasOpen !== isOpen) {
    setWasOpen(isOpen);
    setSearchQuery("");
    setSelectedCandidate(null);
    setSelectedGroup(null);
    setInviteError(null);
    setInviteRole(UserWatchlistRole.Viewer);
  }

  const { members, organizationUsers, excludedUsers } = adapter;

  // SE-8359: the group arm, keyed on the adapter's entity.
  const group = useShareGroupGrants({
    isOpen,
    entityType: adapter.entityType,
    entityId: adapter.entityId,
  });

  /** Every row's user, presence rows included: the group already covers them. */
  const memberUserIds = useMemo(
    (): ReadonlySet<string> => new Set(members.map(member => member.userId)),
    [members]
  );

  /**
   * The people: presence rows (`grantSource: GROUP`) are the group's audience,
   * not grants, so they are not listed; an INACTIVE member keeps the row,
   * marked, for display and revocation only — the grant is not effective
   * access, so the row carries no caption and does not count as a sharee; a
   * person the group outranks carries the caption.
   */
  const accessRows = useMemo(
    (): readonly ShareAccessRowItem[] =>
      members
        .filter(member => !isPresenceRow(member))
        .sort(byRoleThenName)
        .map(member => {
          const isInactive = member.isMembershipActive === false;
          return {
            userWatchlistId: member.userWatchlistId,
            userId: member.userId,
            name: member.name,
            role: member.role,
            isPending: pendingGrantIds.has(member.userWatchlistId),
            isInactive,
            caption: isInactive
              ? null
              : effectiveRoleCaption({
                  role: member.role,
                  groupRole: group.groupRole,
                  groupLabel: group.groupLabel,
                }),
          };
        }),
    [members, pendingGrantIds, group.groupRole, group.groupLabel]
  );

  const hasSharees =
    group.groupRows.length > 0 ||
    accessRows.some(
      row => row.role !== UserWatchlistRole.Owner && !row.isInactive
    );

  /**
   * "Ready" only once BOTH reads answered: an unanswered group read is not
   * "no group grant", so neither the empty state nor the group candidate may
   * be published on it.
   */
  const accessState = combineShareAccessStates(
    adapter.accessState,
    group.accessState
  );

  const needle = searchQuery.trim().toLowerCase();

  /** The one group v1 offers, pinned while the query is empty or names it. */
  const groupCandidate = useMemo((): ShareGroupCandidate | null => {
    const candidate = group.eligibleGroups[0];
    if (!candidate) return null;
    return groupCandidateMatches(candidate.label, searchQuery)
      ? candidate
      : null;
  }, [group.eligibleGroups, searchQuery]);

  /** Suggestions: people without access whose name contains the query. */
  const candidates = useMemo((): readonly ShareCandidateItem[] => {
    if (!needle) return [];
    return organizationUsers
      .filter(
        user =>
          user.userId !== currentUserId &&
          !memberUserIds.has(user.userId) &&
          user.name.toLowerCase().includes(needle)
      )
      .slice(0, MAX_SUGGESTIONS);
  }, [organizationUsers, needle, currentUserId, memberUserIds]);

  /** Excluded people the query would otherwise have matched. */
  const excludedMatches = useMemo(
    (): readonly ShareCandidateItem[] =>
      needle
        ? excludedUsers.filter(user => user.name.toLowerCase().includes(needle))
        : [],
    [excludedUsers, needle]
  );
  const inviteCaption =
    candidates.length === 0 && excludedMatches.length > 0
      ? adapter.excludedCaption
      : null;

  const refetchSurfaces = useCallback(() => {
    if (adapter.refetchQueryNames.length === 0) return;
    void apolloClient
      .refetchQueries({ include: [...adapter.refetchQueryNames] })
      .catch(() => undefined);
  }, [apolloClient, adapter.refetchQueryNames]);

  const setPending = useCallback((grantId: string, pending: boolean) => {
    setPendingGrantIds(previous => {
      if (previous.has(grantId) === pending) return previous;
      return pending
        ? new Set([...previous, grantId])
        : new Set([...previous].filter(id => id !== grantId));
    });
  }, []);

  const handleSearchChange = useCallback((next: string) => {
    setSearchQuery(next);
    setSelectedCandidate(null);
    setSelectedGroup(null);
    setInviteError(null);
  }, []);

  const handleSelectCandidate = useCallback(
    (userId: string) => {
      const candidate = candidates.find(item => item.userId === userId);
      if (!candidate) return;
      setSelectedCandidate(candidate);
      setSelectedGroup(null);
      setSearchQuery(candidate.name);
      setInviteError(null);
    },
    [candidates]
  );

  const handleSelectGroup = useCallback(() => {
    if (!groupCandidate) return;
    setSelectedGroup(groupCandidate);
    setSelectedCandidate(null);
    setSearchQuery(groupCandidate.label);
    setInviteError(null);
  }, [groupCandidate]);

  const handleClearCandidate = useCallback(() => {
    setSelectedCandidate(null);
    setSelectedGroup(null);
    setSearchQuery("");
    setInviteError(null);
  }, []);

  const handleInviteRoleChange = useCallback((value: string) => {
    if (isShareGrantRole(value)) setInviteRole(value);
  }, []);

  /** Invites the picked group (SE-8359): one `createGroupGrant`, then the same after-care as a person. */
  const inviteGroup = useCallback(
    async (target: ShareGroupCandidate): Promise<void> => {
      const inviteKey = `${adapter.entityId}:group:${target.key}`;
      if (inFlightInvites.current.has(inviteKey)) {
        setInviteError(`${target.label} is already being invited.`);
        return;
      }
      inFlightInvites.current = new Set([
        ...inFlightInvites.current,
        inviteKey,
      ]);
      setInviteError(null);
      setIsInviting(true);
      try {
        await group.invite(target, inviteRole);
        setSelectedGroup(null);
        setSearchQuery("");
        refetchSurfaces();
        const verb =
          inviteRole === UserWatchlistRole.Editor ? "can edit" : "can view";
        toast.show({
          action: "success",
          description: `${target.label} ${verb} ${entityName}`,
        });
      } catch (error) {
        setInviteError(
          groupGrantFailureMessage(
            error,
            `Couldn't invite ${target.label}. Try again.`
          )
        );
        toast.show({
          action: "danger",
          description: "Failed to update sharing",
        });
      } finally {
        inFlightInvites.current = new Set(
          [...inFlightInvites.current].filter(key => key !== inviteKey)
        );
        setIsInviting(false);
      }
    },
    [adapter.entityId, group, inviteRole, refetchSurfaces, toast, entityName]
  );

  const handleInvite = useCallback(async (): Promise<void> => {
    if (selectedGroup) {
      await inviteGroup(selectedGroup);
      return;
    }
    // A typed name that exactly matches an excluded person gets the caption,
    // not "no one named X" — the person exists, the object refuses them.
    const excludedExact =
      selectedCandidate === null && needle
        ? excludedMatches.find(user => user.name.toLowerCase() === needle)
        : undefined;
    if (excludedExact && adapter.excludedCaption) {
      setInviteError(adapter.excludedCaption);
      return;
    }
    const resolution = resolveInvitee({
      selected: selectedCandidate,
      query: searchQuery,
      candidates,
      organizationUsers,
      memberUserIds,
      currentUserId,
      entityNoun: adapter.entityNoun,
    });
    if (!resolution.candidate) {
      setInviteError(resolution.error);
      return;
    }
    const { candidate } = resolution;
    const inviteKey = `${adapter.entityId}:${candidate.userId}`;
    if (inFlightInvites.current.has(inviteKey)) {
      setInviteError(`${candidate.name} is already being invited.`);
      return;
    }
    inFlightInvites.current = new Set([...inFlightInvites.current, inviteKey]);
    setInviteError(null);
    setIsInviting(true);
    try {
      await adapter.invite(candidate, inviteRole);
      setSelectedCandidate(null);
      setSearchQuery("");
      refetchSurfaces();
      const verb =
        inviteRole === UserWatchlistRole.Editor ? "can edit" : "can view";
      toast.show({
        action: "success",
        description: `${candidate.name} ${verb} ${entityName}`,
      });
    } catch (error) {
      setInviteError(
        (adapter.inviteFailureMessage ?? defaultInviteFailureMessage)(
          candidate,
          error
        )
      );
      toast.show({ action: "danger", description: "Failed to update sharing" });
    } finally {
      inFlightInvites.current = new Set(
        [...inFlightInvites.current].filter(key => key !== inviteKey)
      );
      setIsInviting(false);
    }
  }, [
    selectedGroup,
    inviteGroup,
    selectedCandidate,
    needle,
    excludedMatches,
    adapter,
    searchQuery,
    candidates,
    organizationUsers,
    memberUserIds,
    currentUserId,
    inviteRole,
    refetchSurfaces,
    toast,
    entityName,
  ]);

  const handleChangeRole = useCallback(
    async (grantId: string, value: string): Promise<void> => {
      const row = members.find(member => member.userWatchlistId === grantId);
      if (
        !row ||
        row.role === UserWatchlistRole.Owner ||
        !isShareGrantRole(value) ||
        row.role === value
      ) {
        return;
      }
      const previousRole = row.role;
      const grantRef = apolloClient.cache.identify({
        __typename: adapter.grantTypename,
        id: grantId,
      });
      const writeRole = (role: UserWatchlistRole) => {
        apolloClient.cache.modify({
          id: grantRef,
          fields: { role: () => role },
        });
      };
      setPending(grantId, true);
      writeRole(value);
      try {
        await adapter.changeRole(grantId, value);
        refetchSurfaces();
      } catch {
        writeRole(previousRole);
        toast.show({
          action: "danger",
          description: `Couldn't change ${row.name}'s role`,
        });
      } finally {
        setPending(grantId, false);
      }
    },
    [members, apolloClient, adapter, setPending, refetchSurfaces, toast]
  );

  const handleRevoke = useCallback(
    async (grantId: string): Promise<void> => {
      const row = members.find(member => member.userWatchlistId === grantId);
      if (!row || row.role === UserWatchlistRole.Owner) return;
      setPending(grantId, true);
      try {
        await adapter.revoke(grantId);
        refetchSurfaces();
      } catch {
        toast.show({
          action: "danger",
          description: `Couldn't remove ${row.name}`,
        });
      } finally {
        setPending(grantId, false);
      }
    },
    [members, adapter, setPending, refetchSurfaces, toast]
  );

  // SE-8359: the group line's writes; the backend's refusal is toasted
  // verbatim (GROUP_GRANT_NOT_FOUND, …), the line stays until the read says.
  const handleChangeGroupRole = useCallback(
    async (groupGrantId: string, value: string): Promise<void> => {
      try {
        await group.changeRole(groupGrantId, value);
        refetchSurfaces();
      } catch (error) {
        toast.show({
          action: "danger",
          description: groupGrantFailureMessage(
            error,
            "Couldn't change the group's role"
          ),
        });
      }
    },
    [group, refetchSurfaces, toast]
  );

  const handleRevokeGroup = useCallback(
    async (groupGrantId: string): Promise<void> => {
      try {
        await group.revoke(groupGrantId);
        refetchSurfaces();
      } catch (error) {
        toast.show({
          action: "danger",
          description: groupGrantFailureMessage(
            error,
            "Couldn't remove the group"
          ),
        });
      }
    },
    [group, refetchSurfaces, toast]
  );

  return {
    accessRows,
    accessState,
    candidates,
    inviteCaption,
    searchQuery,
    selectedCandidate,
    groupCandidate,
    selectedGroup,
    groupRows: group.groupRows,
    hasSharees,
    inviteRole,
    inviteError,
    isInviting,
    onSearchChange: handleSearchChange,
    onSelectCandidate: handleSelectCandidate,
    onSelectGroup: handleSelectGroup,
    onClearCandidate: handleClearCandidate,
    onInviteRoleChange: handleInviteRoleChange,
    onInvite: handleInvite,
    onChangeRole: handleChangeRole,
    onRevoke: handleRevoke,
    onChangeGroupRole: handleChangeGroupRole,
    onRevokeGroup: handleRevokeGroup,
    onDone: onClose,
  };
};
